// eslint-disable-next-line import/no-webpack-loader-syntax
import '!style-loader!css-loader!react-international-phone/style.css';

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import { debounce } from 'lodash';
import {
	PhoneInputProps,
	defaultCountries as originalDefaultCountries,
	parseCountry,
	usePhoneInput,
	CountrySelectorDropdown,
	getCountry,
	FlagEmoji,
} from 'react-international-phone';
import type { ParsedCountry } from 'react-international-phone/build/types';
import css from './PhoneInput.scss';

export const phoneToE164 = (phone: string) => phone.replace(/[^0-9+]/g, '');

const buildClassName = (value: string) => `react-international-phone-${value}`;

interface Props extends Omit<PhoneInputProps, 'onChange'> {
	align?: 'left' | 'right';
	hideCountry?: boolean;
	/**
	 * Render a custom input node to implement specific logic. The initial use case is primarily in CardEditor
	 * to edit the field placeholder without the need to handle the PhoneInput component's logic.
	 */
	renderInput?: (props: { className: string; placeholder: string; 'data-dial-code'?: string }) => React.ReactNode;
	onChange?: (phone: string, country: ParsedCountry) => void;
}

export enum InternationalCountryData {
	name = 'International',
	iso2 = 'un',
	dialCode = '',
}

export const defaultCountries = originalDefaultCountries;

export const DIAL_CODE_DATA_ATTR = 'data-dial-code';

const PhoneInput: React.FC<Props> = ({
	align = document.documentElement.dir === 'rtl' ? 'right' : 'left',
	defaultCountry = document.documentElement.lang === 'he' ? 'il' : 'us',
	...props
}) => {
	const [showDropdown, setShowDropdown] = useState(false);
	const dropdownRef = useRef<HTMLElement>(null);
	const selectorRef = useRef<HTMLDivElement>(null);
	const fullSelectedCountryRef = useRef<ParsedCountry>();

	const { country, phone, setCountry, inputRef, handlePhoneValueChange } = usePhoneInput({
		defaultCountry,
		onChange: data => props.onChange?.(data.phone, fullSelectedCountryRef.current!),
		disableDialCodePrefill: true,
		disableDialCodeAndPrefix: true,
	});

	const placeholder = useMemo(() => {
		const countryData = defaultCountries.find(data => parseCountry(data).iso2 === country);
		const parsedCountry = countryData && parseCountry(countryData);
		let count = -1;
		// eslint-disable-next-line no-plusplus
		return parsedCountry ? `${parsedCountry.format?.replace(/\./g, () => `${Math.min(9, ++count)}`) ?? ''}` : '';
	}, [country]);

	const fullSelectedCountry = useMemo(() => {
		if (!country) return undefined;
		return getCountry({
			value: country,
			field: 'iso2',
			countries: defaultCountries,
		});
	}, [country]);
	fullSelectedCountryRef.current = fullSelectedCountry;

	const renderSelectorButton = () => {
		const isDisabled = props.hideDropdown || props.disabled;
		const rootProps = {
			title: fullSelectedCountry?.name,
			onClick: isDisabled ? undefined : () => setShowDropdown(v => !v),
			// Need this to close dropdown on selector button click
			// https://stackoverflow.com/a/28963938
			onMouseDown: isDisabled ? undefined : (e: React.MouseEvent) => e.preventDefault(),
			onKeyDown: isDisabled
				? undefined
				: (e: React.KeyboardEvent) => {
						if (!e.key) return;

						if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
							e.preventDefault();
							setShowDropdown(true);
						}
				  },
			role: 'combobox',
			'aria-label': 'Country selector',
			'aria-haspopup': 'listbox',
			'aria-expanded': showDropdown,
		} as const;

		return (
			<button
				{...rootProps}
				type="button"
				className={cn(buildClassName('country-selector-button'), {
					[buildClassName('country-selector-button--active')]: showDropdown,
					[buildClassName('country-selector-button--disabled')]: isDisabled,
				})}
				data-country={country}
			>
				<div className={buildClassName('country-selector-button__button-content')}>
					<FlagEmoji
						iso2={country}
						className={buildClassName('country-selector-button__flag-emoji')}
						style={{ visibility: country ? 'visible' : 'hidden' }}
					/>
					<div
						className={cn(buildClassName('country-selector-button__dropdown-arrow'), {
							[buildClassName('country-selector-button__dropdown-arrow--active')]: showDropdown,
						})}
					/>
				</div>
			</button>
		);
	};

	usePositionedDropdown({
		isOpen: showDropdown,
		dropdown: dropdownRef.current?.querySelector(`.${buildClassName('country-selector-dropdown')}`) as HTMLElement,
		button: selectorRef.current as HTMLElement,
		align,
	});

	const inputClassName = cn(buildClassName('input'), props.inputProps?.className, props.inputClassName);
	const inputPlaceholder = props.placeholder ?? placeholder;

	return (
		<div
			className={cn(buildClassName('input-container'), props.className, {
				[css.alignLeft]: align === 'left',
				[css.alignRight]: align === 'right',
				[css.hideCountry]: props.hideCountry,
			})}
		>
			{!props.hideCountry && (
				<div className={buildClassName('country-selector')} ref={selectorRef}>
					{renderSelectorButton()}
					{!props.hideDropdown &&
						createPortal(
							<span ref={dropdownRef}>
								<CountrySelectorDropdown
									show={showDropdown}
									countries={defaultCountries}
									onSelect={_country => {
										setShowDropdown(false);
										setCountry(_country.iso2);
									}}
									selectedCountry={country}
									onClose={() => setShowDropdown(false)}
								/>
							</span>,
							document.body
						)}
				</div>
			)}
			{props.renderInput ? (
				props.renderInput({
					className: inputClassName,
					placeholder: inputPlaceholder,
					[DIAL_CODE_DATA_ATTR]: fullSelectedCountry?.dialCode,
				})
			) : (
				<input
					type="tel"
					{...props.inputProps}
					{...{ [DIAL_CODE_DATA_ATTR]: fullSelectedCountry?.dialCode }}
					className={inputClassName}
					value={phone}
					placeholder={inputPlaceholder}
					onChange={event => {
						handlePhoneValueChange(event);
						props.inputProps?.onChange?.(event);
					}}
					ref={inputRef}
				/>
			)}
		</div>
	);
};

function usePositionedDropdown(props: {
	dropdown?: HTMLElement;
	button?: HTMLElement;
	isOpen: boolean;
	align: 'left' | 'right';
}) {
	useEffect(() => {
		const adjustDropdownPosition = () => {
			if (props.button && props.dropdown && props.isOpen) {
				const buttonRect = props.button.getBoundingClientRect();
				const dropdownRect = props.dropdown.getBoundingClientRect();
				const windowWidth = window.innerWidth;
				const windowHeight = window.innerHeight;

				let left = props.align === 'right' ? buttonRect.right - dropdownRect.width : buttonRect.left;
				let top = buttonRect.bottom;

				if (left + dropdownRect.width > windowWidth) left = windowWidth - dropdownRect.width;
				if (top + dropdownRect.height > windowHeight) top = buttonRect.top - dropdownRect.height;

				/* eslint-disable no-param-reassign */
				props.dropdown.style.left = `${left}px`;
				props.dropdown.style.top = `${top}px`;
				/* eslint-enable no-param-reassign */
			}
		};
		const debouncedAdjustDropdownPosition = debounce(adjustDropdownPosition, 150);

		adjustDropdownPosition();
		window.addEventListener('resize', debouncedAdjustDropdownPosition);
		window.addEventListener('scroll', debouncedAdjustDropdownPosition);

		return () => {
			window.removeEventListener('resize', debouncedAdjustDropdownPosition);
			window.removeEventListener('scroll', debouncedAdjustDropdownPosition);
		};
	}, [props]);

	return null;
}

export default PhoneInput;
